'use client';

import { useState } from 'react';
import Link from 'next/link';
import StayLinkButton from './StayLinkButton';
import BookingLinkButton from './BookingLinkButton';
import StayProviderCard from '@/components/accommodation/StayProviderCard';
import CountryFAQ from '@/components/country/CountryFAQ';
import GfSectionMark from '@/components/common/GfSectionMark';
import GfSubnav from '@/components/common/GfSubnav';
import GfVerticalHero from '@/components/common/GfVerticalHero';
import { bentoSizes } from '@/components/common/GfBento';
import { t } from '@/i18n/translations';

const TYPE_MAP = {
    'Отели': 'hotel', 'Hotels': 'hotel',
    'Апартаменты': 'apartment', 'Apartments': 'apartment',
    'Гостевые дома': 'guesthouse', 'Guesthouses': 'guesthouse',
    'Виллы': 'villa', 'Villas': 'villa',
};

export default function AccommodationPageContent({
    content,
    lang = 'en',
    background,
    countrySlug,
    citySlug,
    countryData,
    cityData,
    affiliateProvider,
    affiliateProviders = [],
    showFaq = true,
    showPartnerAfterHero = false,
    hasTypesSection = false,
    extraAnchors = [],
    localNotes = null,
}) {
    const isRu = lang === 'ru';
    const sp = t(lang, 'servicePage');   // общие тексты сервис-страниц
    const vp = t(lang, 'stayPage');      // специфика жилья
    const [showAllCities, setShowAllCities] = useState(false);
    const heroBg = background || '/images/my-hero.jpg';
    const cName = content.countryName
        || (isRu ? countryData?.nameRu : countryData?.nameEn)
        || countryData?.name
        || (countrySlug ? countrySlug.charAt(0).toUpperCase() + countrySlug.slice(1) : '');

    const providersToShow = Array.isArray(affiliateProviders) && affiliateProviders.length > 0
        ? affiliateProviders
        : (affiliateProvider ? [affiliateProvider] : []);

    const cities = Array.isArray(content.cities) ? content.cities : [];
    const citiesToShow = showAllCities ? cities : cities.slice(0, 6);
    const types = Array.isArray(content.types) ? content.types : [];
    const picks = Array.isArray(content.picks) ? content.picks : [];

    const has = {
        providers: showPartnerAfterHero && providersToShow.length > 0,
        cities: cities.length > 0,
        types: hasTypesSection || (citySlug && types.length > 0),
        faq: showFaq && Array.isArray(content.faq) && content.faq.length > 0,
    };

    const anchors = [
        has.providers && { href: '#providers', label: vp.anchor.services },
        has.cities && { href: '#cities', label: vp.anchor.cities },
        has.types && { href: '#types', label: vp.anchor.types },
        ...extraAnchors,
        { href: '#faq', label: sp.anchor.faq },
        { href: '#articles', label: sp.anchor.articles },
    ].filter(Boolean);

    return (
        <div className="gf-root">
            {/* ── HERO ─────────────────────────────────────────── */}
            <GfVerticalHero background={heroBg} title={content.title} sub={content.description}>
                {countrySlug && citySlug && (
                    <StayLinkButton
                        variant="bare"
                        countrySlug={countrySlug}
                        citySlug={citySlug}
                        siteLang={lang}
                        countryData={countryData}
                        cityData={cityData}
                        affiliateProvider={affiliateProvider}
                        linkParams={{ mode: 'base', subId: `${citySlug}-all` }}
                        className="pri"
                    >
                        {vp.heroBook(isRu ? (content.cityNameInCase || content.cityName) : content.cityName)}
                    </StayLinkButton>
                )}
                {/* Хаб страны был без кнопки, первая внешняя ссылка — на 1.6 экрана (обход 01.10.2026):
                    ведём к городам, где у каждой карточки кнопки Trip.com и Booking */}
                {!citySlug && has.cities && <a className="pri" href="#cities">{vp.heroCities} <b>↓</b></a>}
            </GfVerticalHero>

            {/* ── SUBNAV ───────────────────────────────────────── */}
            <GfSubnav lang={lang} leaf={vp.leaf} anchors={anchors}
                crumbs={[cName && countrySlug && { href: `/${lang}/${countrySlug}`, label: cName }]} />

            {/* ── PARTNERS ─────────────────────────────────────── */}
            {has.providers && (
                <section className="gf-sec" id="providers">
                    <GfSectionMark eyebrow={vp.eyebrow.services} title={content.providersTitle || vp.servicesTitle} />
                    {content.providersSubtitle && <p className="gf-lead">{content.providersSubtitle}</p>}
                    <div className={providersToShow.length <= 2 ? 'gf-prov-center' : 'gf-grid'} style={{ marginTop: 28 }}>
                        {providersToShow.map((provider) => (
                            <StayProviderCard
                                key={provider._id || provider.code}
                                provider={provider}
                                lang={lang}
                                countrySlug={countrySlug}
                                countryName={cName}
                                countryData={countryData}
                                affiliateProvider={provider}
                                className="h-full"
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* ── CITIES ───────────────────────────────────────── */}
            {has.cities && (
                <section className="gf-sec" id="cities" style={{ paddingTop: has.providers ? 0 : undefined }}>
                    <GfSectionMark eyebrow={vp.eyebrow.cities} title={content.citiesTitle || vp.citiesTitle} />
                    {content.citiesSubtitle && <p className="gf-lead">{content.citiesSubtitle}</p>}
                    {(() => {
                        const sizes = bentoSizes(citiesToShow.length);
                        return (
                            <div className="bl-bento ex-all stay-cities">
                                {citiesToShow.map((city, i) => (
                                    <article
                                        key={city.slug}
                                        className={`mz ${sizes[i] || 'std'}`}
                                    >
                                        {city.image && <img src={city.image} alt={city.name} loading="lazy" />}
                                        <span className="scrim" />
                                        <div className="mz-b">
                                            <h3>{city.name}</h3>
                                            {city.description && <p className="mz-ex">{city.description}</p>}
                                            <div className="mz-actions">
                                                {city.hasHotels && (
                                                    <Link className="mz-action" href={`/${lang}/${countrySlug}/${city.slug}/hotels`}>
                                                        {vp.bestHotels}
                                                    </Link>
                                                )}
                                                <StayLinkButton
                                                    variant="bare"
                                                    className="mz-action ghost"
                                                    countrySlug={countrySlug}
                                                    citySlug={city.slug}
                                                    siteLang={lang}
                                                    countryData={countryData}
                                                    cityData={city}
                                                    affiliateProvider={affiliateProvider}
                                                    linkParams={{ mode: 'base', subId: `${city.slug}-city` }}
                                                >
                                                    Trip.com
                                                </StayLinkButton>
                                                {/* Booking как второй агрегатор: часть аудитории бронирует
                                                    только там; до 2026-07 Booking был лишь на city/hotels-
                                                    страницах (4 клика за 90 дней — кнопку никто не видел) */}
                                                <BookingLinkButton
                                                    variant="bare"
                                                    className="mz-action ghost"
                                                    cityName={city.stay?.tripCityName || city.name}
                                                    countryName={countryData?.nameEn || countryData?.name}
                                                    countryCurrency={countryData?.currency}
                                                    citySlug={city.slug}
                                                    siteLang={lang}
                                                    subIdSuffix="stay-city"
                                                >
                                                    Booking
                                                </BookingLinkButton>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        );
                    })()}
                    {cities.length > 6 && (
                        <div className="gf-more-wrap">
                            <button type="button" className="gf-more" onClick={() => setShowAllCities(!showAllCities)}>
                                {showAllCities ? vp.showLess : vp.showMore(cities.length - 6)}
                            </button>
                        </div>
                    )}
                </section>
            )}

            {/* ── LOCAL STAY NOTES (country-unique) ────────────── */}
            {localNotes && (
                <section className="gf-sec gf-localnet" id="local" style={{ paddingTop: (has.providers || has.cities) ? 0 : undefined }}>
                    <GfSectionMark eyebrow={localNotes.eyebrow} tag={cName || ''} title={localNotes.heading} />
                    {localNotes.intro && <p className="gf-lead">{localNotes.intro}</p>}
                    {localNotes.points.length > 0 && (
                        <ul className="gf-localnet-list">
                            {localNotes.points.map((point, i) => (
                                <li key={i}>{point}</li>
                            ))}
                        </ul>
                    )}
                    {localNotes.pricing && <p className="gf-localnet-pricing">{localNotes.pricing}</p>}
                    {localNotes.outro && <p className="gf-localnet-outro">{localNotes.outro}</p>}
                </section>
            )}

            {/* ── TYPES (city pages) ───────────────────────────── */}
            {citySlug && types.length > 0 && (
                <section className="gf-sec" id="types" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.eyebrow.types} title={content.typesTitle || vp.typesTitle} />
                    <div className="gf-scen">
                        {types.map((type) => {
                            const tripType = TYPE_MAP[type.title] || 'hotel';
                            const locationSlug = citySlug || countrySlug;
                            return (
                                <StayLinkButton
                                    key={type.title}
                                    variant="bare"
                                    countrySlug={countrySlug}
                                    citySlug={citySlug}
                                    siteLang={lang}
                                    countryData={countryData}
                                    cityData={cityData}
                                    affiliateProvider={affiliateProvider}
                                    linkParams={{ mode: 'keyword', keyword: tripType, subId: `${locationSlug}-${tripType}` }}
                                    className="sc"
                                >
                                    <div className="ri-row"><span style={{ fontSize: 22 }}>{type.icon}</span><h3>{type.title}</h3></div>
                                    {type.description && <p>{type.description}</p>}
                                    <span className="go">{vp.view}</span>
                                </StayLinkButton>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* ── PICKS (city pages) ───────────────────────────── */}
            {citySlug && picks.length > 0 && (
                <section className="gf-sec" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.eyebrow.picks} title={content.picksTitle || vp.picksTitle} />
                    <div className="gf-arts">
                        {picks.map((pick) => (
                            <StayLinkButton
                                key={pick.slug}
                                variant="bare"
                                className="ar"
                                countrySlug={countrySlug}
                                citySlug={citySlug}
                                siteLang={lang}
                                countryData={countryData}
                                cityData={cityData}
                                affiliateProvider={affiliateProvider}
                                linkParams={{ mode: 'keyword', keyword: 'hotel', subId: `${pick.slug}-pick` }}
                            >
                                <div className="ph">
                                    {pick.image && <img src={pick.image} alt={pick.name} loading="lazy" />}
                                    <span className="tag">{vp.stayTag}</span>
                                </div>
                                <div className="ar-body">
                                    <h3>{pick.name}</h3>
                                    {pick.description && <p className="ex">{pick.description}</p>}
                                    <span className="read">{vp.findHotelsNearby}</span>
                                </div>
                            </StayLinkButton>
                        ))}
                    </div>
                </section>
            )}

            {/* ── FAQ (city pages render here; country renders in page-module) ── */}
            {has.faq && (
                <section className="gf-sec" id="faq" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.qa} tag={sp.tag.faq} title={sp.popularQuestions} />
                    {content.faqSubtitle && <p className="gf-lead">{content.faqSubtitle}</p>}
                    <CountryFAQ faqItems={content.faq.map((it) => ({ question: it.q ?? it.question, answer: it.a ?? it.answer }))} lang={lang} variant="gf" />
                </section>
            )}
        </div>
    );
}
